import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { SuperadminGuide } from "../admin/guide/SuperadminGuide";
import { Hierarchy } from "../admin/hierarchy/Hierarchy";
import { Organogram } from "../admin/organogram/Organogram";
import { People } from "../admin/people/People";
import { PollingStations } from "../admin/pollingStations/PollingStations";
import { Races } from "../admin/races/Races";
import { Assignments } from "../admin/rbac/Assignments";
import { MapScreen } from "../map/MapScreen";
import { NewUserWizard } from "../admin/rbac/NewUserWizard";
import { Positions } from "../admin/rbac/Positions";
import { Roles } from "../admin/rbac/Roles";
import { Users } from "../admin/rbac/Users";
import { MenuEditor } from "../admin/screens/MenuEditor";
import { ScreensMatrix } from "../admin/screens/ScreensMatrix";
import { CasesRoutes } from "../ops/cases/Cases";
import { VotersRoutes } from "../ops/voters/Voters";
import { IntelFeed } from "../ops/intel/IntelFeed";
import { PerformanceRoutes } from "../ops/performance/Performance";
import { POIRoutes } from "../ops/poi/POI";
import { PopularityRoutes } from "../ops/popularity/Popularity";
import { AuditLog } from "../ops/reports/AuditLog";
import { Reports } from "../ops/reports/Reports";
import { Threats } from "../ops/reports/Threats";
import { TasksRoutes } from "../ops/tasks/Tasks";
import { NewItemWizard } from "../ops/new/Wizard";
import { ApiError, api } from "../api/client";
import { enableWebPush } from "../notifications/fcm";
import { Home } from "./Home";
import { NothingToSeeHere } from "./NothingToSeeHere";
import { Sidebar, type MenuNode } from "./Sidebar";
import { MenuIcon } from "./icons";

interface MenuResponse { menu: MenuNode[]; }
interface MeResponse {
  uid: string;
  email: string | null;
  isSuperadmin: boolean;
  permissions: string[];
}

export function Shell() {
  const me = useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/me/permissions"),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const menu = useQuery<MenuResponse>({
    queryKey: ["menu"],
    queryFn: () => api<MenuResponse>("/me/menu"),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Try to enable web push once after the user is signed in. If the browser
  // doesn't support it or the user denies the permission prompt, this fails
  // quietly. The first call shows the permission dialog; subsequent calls
  // are no-ops.
  useEffect(() => {
    if (me.data?.uid) enableWebPush().catch(() => { /* ignore */ });
  }, [me.data?.uid]);

  if (me.isLoading || menu.isLoading) {
    return (
      <div className="centered">
        <div style={{ textAlign: "center", color: "var(--muted)", display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
          <div className="skeleton" style={{ width: 120, height: 12 }} />
          <div className="skeleton" style={{ width: 200, height: 10 }} />
        </div>
      </div>
    );
  }
  if (me.error || menu.error) return <LoadFailure meError={me.error} menuError={menu.error} />;

  return (
    <ShellLayout
      menu={menu.data?.menu ?? []}
      email={me.data?.email}
      isSuperadmin={!!me.data?.isSuperadmin}
    >
      <main>
        <Routes>
          <Route path="/" element={<Home superadmin={!!me.data?.isSuperadmin} email={me.data?.email} menu={menu.data?.menu ?? []} />} />
          <Route path="new" element={<NewItemWizard />} />
          <Route path="admin/hierarchy/*" element={<Hierarchy />} />
          <Route path="admin/roles" element={<Roles />} />
          <Route path="admin/positions" element={<Positions />} />
          <Route path="admin/races" element={<Races />} />
          <Route path="admin/polling-stations" element={<PollingStations />} />
          <Route path="admin/users" element={<Users />} />
          <Route path="admin/users/new" element={<NewUserWizard />} />
          <Route path="admin/assignments" element={<Assignments />} />
          <Route path="admin/screens" element={<ScreensMatrix />} />
          <Route path="admin/menu" element={<MenuEditor />} />
          <Route path="admin/guide" element={<SuperadminGuide />} />
          <Route path="admin/people" element={<People />} />
          <Route path="admin/organogram" element={<Organogram />} />
          <Route path="cases/*" element={<CasesRoutes />} />
          <Route path="voters/*" element={<VotersRoutes />} />
          <Route path="poi/*" element={<POIRoutes />} />
          <Route path="tasks/*" element={<TasksRoutes />} />
          <Route path="performance/*" element={<PerformanceRoutes />} />
          <Route path="popularity/*" element={<PopularityRoutes />} />
          <Route path="intel" element={<IntelFeed />} />
          <Route path="reports" element={<Reports />} />
          <Route path="threats" element={<Threats />} />
          <Route path="map" element={<MapScreen />} />
          <Route path="audit" element={<AuditLog />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </ShellLayout>
  );
}

/**
 * Wraps the sidebar + main content with mobile drawer behavior.
 * On screens > 768px the drawer state has no effect (CSS hides it).
 * On mobile, the hamburger toggles `drawer-open` which slides the sidebar in.
 */
function ShellLayout({
  menu,
  email,
  isSuperadmin,
  children,
}: {
  menu: MenuNode[];
  email: string | null | undefined;
  isSuperadmin: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Auto-close the drawer when the user navigates to a new route on mobile.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className={`shell${open ? " drawer-open" : ""}`}>
      <button
        className="hamburger"
        aria-label="Open menu"
        onClick={() => setOpen((v) => !v)}
      >
        <MenuIcon />
      </button>
      <div className="sidebar-backdrop" onClick={() => setOpen(false)} aria-hidden />
      <Sidebar menu={menu} email={email} isSuperadmin={isSuperadmin} />
      {children}
    </div>
  );
}

function LoadFailure({ meError, menuError }: { meError: unknown; menuError: unknown }) {
  function format(label: string, err: unknown) {
    if (!err) return null;
    const isApi = err instanceof ApiError;
    return (
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title" style={{ color: "var(--danger)" }}>{label}</div>
        {isApi ? (
          <>
            <div className="card-sub" style={{ marginBottom: 8 }}>
              <code>HTTP {err.status}</code>{" "}
              {err.code && <code style={{ marginLeft: 6 }}>{err.code}</code>}
              {err.requestId && (
                <code style={{ marginLeft: 6, color: "var(--muted)" }}>req {err.requestId}</code>
              )}
            </div>
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{err.message}</div>
            {(err.errorClass || err.errorLocation) && (
              <div style={{ marginTop: 10, padding: 8, background: "var(--bg-muted, #fff5f5)", borderRadius: 4, fontSize: 12 }}>
                {err.errorClass && (
                  <div><strong>Exception:</strong> <code>{err.errorClass}</code></div>
                )}
                {err.errorMessage && err.errorMessage !== err.message && (
                  <div><strong>Message:</strong> <code>{err.errorMessage}</code></div>
                )}
                {err.errorLocation && (
                  <div><strong>Where:</strong> <code>{err.errorLocation}</code></div>
                )}
              </div>
            )}
            {err.tracebackLines && err.tracebackLines.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)" }}>traceback</summary>
                <pre style={{ fontSize: 11, marginTop: 6, color: "var(--muted)", whiteSpace: "pre-wrap" }}>
                  {err.tracebackLines.join("")}
                </pre>
              </details>
            )}
          </>
        ) : err instanceof Error ? (
          <>
            <div className="card-sub" style={{ marginBottom: 8 }}>{err.name}</div>
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{err.message}</div>
            {err.stack && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)" }}>stack</summary>
                <pre style={{ fontSize: 11, marginTop: 6, color: "var(--muted)", whiteSpace: "pre-wrap" }}>{err.stack}</pre>
              </details>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13 }}>{String(err)}</div>
        )}
      </div>
    );
  }
  return (
    <div style={{ padding: 32, maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 4px" }}>Failed to load</h1>
      <p className="muted" style={{ margin: "0 0 24px", fontSize: 14 }}>
        Couldn't fetch your permissions or menu. Details below.
      </p>
      {format("/me/permissions", meError)}
      {format("/me/menu", menuError)}
      <button onClick={() => location.reload()} style={{ marginTop: 8 }}>Reload</button>
    </div>
  );
}

function NotFound() {
  return <NothingToSeeHere />;
}
